import React, { useCallback, useEffect, useState } from "react";
import { Card, CardBody, Container, Input, Spinner, Table, Button, Badge } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import { fetchRefundHistory } from "../../api/requests/payment-requests";
import { fetchCompaniesList } from "../../api/requests/company-requests";
import { fetchAuthMe } from "../../api/requests/auth-requests";
import { formatAmount, formatDateTime } from "./Paymenthistoryutils";
import { DEFAULT_PAGE_SIZES } from "./Paymenthistoryconstants";
import "../../assets/css/page-common.css";
import DateInput from "../../components/Common/DateInput";
import { refundActorLabel } from "../../utils/refundActor";

/**
 * İade Geçmişi — tüm sanal POS iadelerinin denetlenebilir listesi.
 *
 * Sağlayıcıdan bağımsızdır: PayTR, Garanti BBVA ve İşyerimPOS iadeleri backend'de
 * ortak `payment_refunds` kaydını ürettiği için tek modelden beslenir.
 *
 * Filtreleme/sayfalama/sıralama tamamen SERVER-SIDE'dır — iade geçmişi zamanla büyür,
 * tüm kayıtları çekip tarayıcıda süzmek ölçeklenmez.
 *
 * Yetki: erişim iade YAPMA yetkisiyle aynı rollere (SUPER_ADMIN, COMPANY_ADMIN,
 * COMPANY_ACCOUNTANT) sınırlıdır ve asıl sınır backend'dedir. Buradaki `companyId`
 * filtresi yalnızca SUPER_ADMIN'e gösterilir; diğer roller için backend kapsamı
 * oturumdan zorlar (başka firma istenirse 403).
 */

const STATUS_META = {
    SUCCESS: { label: "Başarılı", color: "success" },
    FAILED: { label: "Başarısız", color: "danger" },
    PENDING: { label: "Bekliyor", color: "warning" },
};

const PROVIDER_LABELS = {
    PAYTR: "PayTR",
    GARANTI: "Garanti BBVA",
    ISYERIM_POS: "İşyerimPOS",
    PARAMPOS: "ParamPOS",
    QNB_PAYFOR: "QNB PayFor",
    ESNEK_POS: "EsnekPos",
    IYZICO: "iyzico",
};

const EMPTY_FILTERS = {
    startDate: "", endDate: "", companyId: "",
    provider: "", refundClass: "", status: "", search: "",
};

// Özette yalnız fiilen geri ödenen para gösterilir; backend'in döndürdüğü
// total/failed/partial alanları kullanılmaz.
const EMPTY_SUMMARY = { successCount: 0, successAmount: 0 };

const styles = `
  .refund-history .stat-icon {
      width:46px; height:46px; flex:0 0 46px; border-radius:.85rem;
      display:flex; align-items:center; justify-content:center; font-size:1.35rem;
  }
  .refund-history .summary-bar { display:flex; flex-wrap:wrap; align-items:center; gap:.75rem; margin-bottom:1rem; }
  .refund-history .summary-pill {
      display:inline-flex; align-items:center; gap:.55rem; cursor:default;
      background:#EAF7EE; border:1px solid #CBE9D5; border-radius:999px; padding:.4rem .95rem;
  }
  .refund-history .summary-pill .bx { color:#1E7E34; font-size:1.05rem; }
  .refund-history .summary-pill-label { font-size:.72rem; letter-spacing:.04em; text-transform:uppercase; color:#4B6B57; font-weight:600; }
  .refund-history .summary-pill-value { font-size:1rem; font-weight:700; color:#186A2E; line-height:1; }
  .refund-history .summary-pill-dot { width:4px; height:4px; border-radius:50%; background:#A9CDB8; }
  .refund-history .summary-pill-sub { font-size:.78rem; color:#6F8A79; }
  @media (max-width:576px) { .refund-history .summary-pill { width:100%; justify-content:flex-start; } }
  .refund-history .filter-label { font-size:.78rem; color:#6b7280; font-weight:600; margin-bottom:.3rem; }
  .refund-history .sortable-th { cursor:pointer; user-select:none; }
  .refund-history .sortable-th .bx { opacity:.45; }
  .refund-history .sortable-th.is-active .bx { opacity:1; }
  .refund-history .oid-cell { color:#e2547d; font-weight:600; }
  .refund-history table th { white-space:nowrap; }
`;

const RefundHistoryTable = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Rol bilgisi oturum ucundan alınır (Ödeme Geçmişi ile aynı desen); localStorage'a
    // güvenilmez. Firma kolonu/filtresi yalnızca görünürlük içindir — asıl sınır backend'de.
    const [currentUser, setCurrentUser] = useState(null);
    const isSuperAdmin = () => currentUser?.roles?.includes("ROLE_SUPER_ADMIN");

    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 0);
    const [pageSize, setPageSize] = useState(Number(searchParams.get("size")) || 10);
    const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "requestedAt");
    const [sortDir, setSortDir] = useState(searchParams.get("sortDir") || "desc");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [companies, setCompanies] = useState([]);

    // Filtreler taslak + uygulanmış olarak ayrılır: her tuşa basışta istek atılmaz,
    // arama alanı "Uygula" ile gönderilir (Ödeme Geçmişi ile aynı davranış).
    const [draft, setDraft] = useState({
        ...EMPTY_FILTERS,
        startDate: searchParams.get("startDate") || "",
        endDate: searchParams.get("endDate") || "",
        companyId: searchParams.get("companyId") || "",
        provider: searchParams.get("provider") || "",
        refundClass: searchParams.get("refundClass") || "",
        status: searchParams.get("status") || "",
        search: searchParams.get("search") || "",
    });
    const [applied, setApplied] = useState(draft);

    const syncUrl = useCallback((filters, page, size, sBy, sDir) => {
        const params = new URLSearchParams();
        Object.entries({ ...filters, page, size, sortBy: sBy, sortDir: sDir }).forEach(([k, v]) => {
            if (v !== "" && v !== null && v !== undefined
                && !(k === "page" && v === 0) && !(k === "size" && v === 10)
                && !(k === "sortBy" && v === "requestedAt") && !(k === "sortDir" && v === "desc")) {
                params.set(k, String(v));
            }
        });
        setSearchParams(params, { replace: true });
    }, [setSearchParams]);

    const load = useCallback(async (page, size, filters, sBy, sDir, superAdmin) => {
        setLoading(true);
        setError("");
        try {
            // Tarih alanları ISO (yyyy-MM-dd) taşır; backend'in beklediği
            // ISO date-time formuna tamamlanır; bitiş günü gün sonuna kadar dahildir.
            const params = {
                page, size, sortBy: sBy, sortDir: sDir,
                ...(filters.startDate && { startDate: `${filters.startDate}T00:00:00` }),
                ...(filters.endDate && { endDate: `${filters.endDate}T23:59:59` }),
                ...(filters.companyId && superAdmin && { companyId: filters.companyId }),
                ...(filters.provider && { provider: filters.provider }),
                ...(filters.refundClass && { refundClass: filters.refundClass }),
                ...(filters.status && { status: filters.status }),
                ...(filters.search && { search: filters.search }),
            };
            const data = await fetchRefundHistory(params);
            if (data.statusCode === 200) {
                const d = data.data;
                setRows(d.content || []);
                setSummary(d.summary || EMPTY_SUMMARY);
                setTotalElements(d.totalElements || 0);
                setTotalPages(d.totalPages || 0);
                setCurrentPage(d.page || 0);
                setPageSize(d.size || size);
                // Backend uyguladığı sıralamayı geri verir; başlıktaki ok tahmine dayanmaz.
                if (d.sortBy) setSortBy(d.sortBy);
                if (d.sortDir) setSortDir(d.sortDir);
            } else {
                setError(data.message || "İade geçmişi getirilemedi");
                setRows([]);
                setSummary(EMPTY_SUMMARY);
            }
        } catch (e) {
            setError("İade geçmişi getirilemedi");
            setRows([]);
            setSummary(EMPTY_SUMMARY);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = "İade Geçmişi | Tahsilat360";

        fetchAuthMe()
            .then(r => (r.ok ? r.json() : null))
            .then(j => {
                const me = j?.data || null;
                setCurrentUser(me);
                const superAdmin = !!me?.roles?.includes("ROLE_SUPER_ADMIN");
                // Liste rol belli olduktan sonra çekilir: aksi halde URL'den gelen
                // companyId filtresi ilk istekte sessizce düşerdi.
                load(currentPage, pageSize, applied, sortBy, sortDir, superAdmin);
                // Firma listesi yalnız SUPER_ADMIN'in işine yarar.
                if (superAdmin) {
                    return fetchCompaniesList()
                        .then(r => (r.ok ? r.json() : null))
                        .then(cj => setCompanies(cj?.data || []));
                }
                return null;
            })
            .catch(() => {
                // Rol alınamazsa firma kapsamı backend'de zaten zorlanır; liste yine çekilir.
                load(currentPage, pageSize, applied, sortBy, sortDir, false);
            });
    }, []);

    const applyFilters = () => {
        setApplied(draft);
        syncUrl(draft, 0, pageSize, sortBy, sortDir);
        load(0, pageSize, draft, sortBy, sortDir, isSuperAdmin());
    };

    const clearFilters = () => {
        setDraft(EMPTY_FILTERS);
        setApplied(EMPTY_FILTERS);
        syncUrl(EMPTY_FILTERS, 0, pageSize, sortBy, sortDir);
        load(0, pageSize, EMPTY_FILTERS, sortBy, sortDir, isSuperAdmin());
    };

    const goToPage = (page) => {
        syncUrl(applied, page, pageSize, sortBy, sortDir);
        load(page, pageSize, applied, sortBy, sortDir, isSuperAdmin());
    };

    const changeSize = (size) => {
        setPageSize(size);
        syncUrl(applied, 0, size, sortBy, sortDir);
        load(0, size, applied, sortBy, sortDir, isSuperAdmin());
    };

    // Aynı kolona tekrar tıklamak yönü çevirir; farklı kolon azalan olarak başlar.
    const toggleSort = (key) => {
        const nextDir = sortBy === key ? (sortDir === "asc" ? "desc" : "asc") : "desc";
        setSortBy(key);
        setSortDir(nextDir);
        syncUrl(applied, 0, pageSize, key, nextDir);
        load(0, pageSize, applied, key, nextDir, isSuperAdmin());
    };

    const SortableTh = ({ label, sortKey, className = "" }) => (
        <th
            className={`sortable-th ${sortBy === sortKey ? "is-active" : ""} ${className}`}
            onClick={() => toggleSort(sortKey)}
            title="Sıralamak için tıklayın"
        >
            {label}{" "}
            <i className={`bx ${sortBy === sortKey
                ? (sortDir === "asc" ? "bx-sort-up" : "bx-sort-down")
                : "bx-sort-alt-2"}`} />
        </th>
    );

    const set = (key) => (e) => setDraft(d => ({ ...d, [key]: e.target.value }));

    // Firma kolonu yalnız SUPER_ADMIN'e gösterilir.
    const showCompany = isSuperAdmin();

    // Durum kolonu HERKESTE vardır: liste her statüdeki iadeyi döndürdüğü için, kolon
    // olmadan başarısız bir deneme başarılı bir iadeyle birebir aynı görünür ve "bu para
    // geri gitti" diye okunur. Rozetin altındaki sağlayıcı hata kodu/mesajı ve referans
    // numarası ise teknik ayrıntıdır; yalnız SUPER_ADMIN'e gösterilir.
    const showStatusDetail = isSuperAdmin();

    // Koşulsuz kolonlar: Tarih, Cari, İşlem No, Orijinal, İade Tutarı, Tip, POS,
    // Yapan, Durum, İşlemler = 10.
    const colCount = 10 + (showCompany ? 1 : 0);

    return (
        <div className="page-content refund-history">
            <style>{styles}</style>
            <Container fluid>

                {/* ── Başlık ─────────────────────────────────────────── */}
                <div className="d-flex flex-wrap align-items-start justify-content-between mb-3 gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <div className="stat-icon" style={{ background: "#EEF3FB", color: "#221C5D" }}>
                            <i className="bx bx-refresh" />
                        </div>
                        <div>
                            <h4 className="mb-1">İade Geçmişi</h4>
                            <small className="text-muted">
                                Sanal POS üzerinden yapılan tüm iade işlemlerini görüntüleyin.
                            </small>
                        </div>
                    </div>
                    <Button
                        className="btn-rounded btn-navy"
                        onClick={() => navigate("/payment-history")}
                    >
                        Ödeme Geçmişi
                    </Button>
                </div>

                {/* ── Filtreler ──────────────────────────────────────── */}
                <Card className="rounded-4 mb-3">
                    <CardBody>
                        <div className="d-flex flex-wrap gap-3 align-items-end">
                            <div>
                                <div className="filter-label">Başlangıç</div>
                                <DateInput value={draft.startDate} onChange={(iso) => setDraft(d => ({ ...d, startDate: iso }))} />
                            </div>
                            <div>
                                <div className="filter-label">Bitiş</div>
                                <DateInput value={draft.endDate} onChange={(iso) => setDraft(d => ({ ...d, endDate: iso }))} />
                            </div>

                            {/* Firma filtresi yalnız SUPER_ADMIN'e anlamlıdır; diğer roller
                                zaten kendi firmasına kilitlidir (backend zorlar). */}
                            {showCompany && (
                                <div>
                                    <div className="filter-label">Firma</div>
                                    <Input type="select" value={draft.companyId} onChange={set("companyId")}>
                                        <option value="">Tümü</option>
                                        {companies.map(c => (
                                            <option key={c.id} value={c.id}>{c.companyName}</option>
                                        ))}
                                    </Input>
                                </div>
                            )}

                            <div>
                                <div className="filter-label">Sanal POS</div>
                                <Input type="select" value={draft.provider} onChange={set("provider")}>
                                    <option value="">Tümü</option>
                                    <option value="PAYTR">PayTR</option>
                                    <option value="GARANTI">Garanti BBVA</option>
                                    <option value="ISYERIM_POS">İşyerimPOS</option>
                                </Input>
                            </div>

                            <div>
                                <div className="filter-label">İade Tipi</div>
                                <Input type="select" value={draft.refundClass} onChange={set("refundClass")}>
                                    <option value="">Tümü</option>
                                    <option value="FULL">Tam İade</option>
                                    <option value="PARTIAL">Kısmi İade</option>
                                </Input>
                            </div>

                            <div>
                                <div className="filter-label">Durum</div>
                                <Input type="select" value={draft.status} onChange={set("status")}>
                                    <option value="">Tümü</option>
                                    <option value="SUCCESS">Başarılı</option>
                                    <option value="FAILED">Başarısız</option>
                                    <option value="PENDING">Bekliyor</option>
                                </Input>
                            </div>

                            <div style={{ minWidth: 260, flex: "1 1 260px" }}>
                                <div className="filter-label">Ara</div>
                                <Input
                                    placeholder="İşlem no, firma, cari, referans"
                                    value={draft.search}
                                    onChange={set("search")}
                                    onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                                />
                            </div>

                        </div>

                        <div className="d-flex gap-2 mt-3">
                            <Button className="btn-rounded btn-navy d-flex align-items-center gap-1" onClick={applyFilters}>
                                <i className="bx bx-filter-alt" /> Uygula
                            </Button>
                            <Button className="btn-rounded btn-navy-outline d-flex align-items-center gap-1" onClick={clearFilters}>
                                <i className="bx bx-refresh" /> Temizle
                            </Button>
                        </div>
                    </CardBody>
                </Card>

                {/* ── Tablo ──────────────────────────────────────────── */}
                <Card className="rounded-4">
                    <CardBody>
                        {/* Rozet, görünen sayfayı değil filtrelenmiş kümenin TAMAMINI özetler;
                            tablonun hemen üstünde durduğu için bu ayrım title ile de yazılı. */}
                        <div className="summary-bar">
                            <div
                                className="summary-pill"
                                title="Filtrelenmiş kümenin tamamındaki başarılı iadeler — görünen sayfa değil"
                            >
                                <i className="bx bx-check-circle" />
                                <span className="summary-pill-label">Başarılı İade</span>
                                <span className="summary-pill-value">{formatAmount(summary.successAmount)}</span>
                                <span className="summary-pill-dot" />
                                <span className="summary-pill-sub">{summary.successCount} işlem</span>
                            </div>
                        </div>

                        {error && <div className="alert alert-danger py-2">{error}</div>}

                        <div className="table-responsive">
                            <Table className="align-middle mb-0">
                                <thead>
                                    <tr>
                                        <SortableTh label="İade Tarihi" sortKey="requestedAt" />
                                        {showCompany && <th>Firma</th>}
                                        <th>Cari</th>
                                        <th>İşlem No</th>
                                        <th className="text-end">Orijinal Tutar</th>
                                        <SortableTh label="İade Tutarı" sortKey="refundAmount" className="text-end" />
                                        <th>İade Tipi</th>
                                        <th>Sanal POS</th>
                                        <th>İadeyi Yapan</th>
                                        <th>Durum</th>
                                        <th className="text-end">İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr>
                                            <td colSpan={colCount} className="text-center py-4">
                                                <Spinner size="sm" /> Yükleniyor…
                                            </td>
                                        </tr>
                                    )}

                                    {!loading && rows.length === 0 && (
                                        <tr>
                                            <td colSpan={colCount} className="text-center text-muted py-4">
                                                Kayıt bulunamadı
                                            </td>
                                        </tr>
                                    )}

                                    {!loading && rows.map(r => {
                                        const st = STATUS_META[r.status] || { label: r.status, color: "secondary" };
                                        return (
                                            <tr key={r.refundId}>
                                                {/* Sonuç zamanı varsa o gösterilir: para gerçekte o an geri döndü.
                                                    Henüz sonuçlanmamış (PENDING) iadelerde istek zamanına düşülür. */}
                                                <td>{formatDateTime(r.completedAt || r.requestedAt)}</td>
                                                {showCompany && <td>{r.companyName || "-"}</td>}
                                                <td>{r.accountName || "-"}</td>
                                                <td className="oid-cell">{r.merchantOid}</td>
                                                <td className="text-end">{formatAmount(r.originalAmount)}</td>
                                                <td className="text-end fw-semibold">{formatAmount(r.refundAmount)}</td>
                                                <td>
                                                    <Badge color={r.refundClass === "FULL" ? "dark" : "info"} pill>
                                                        {r.refundClass === "FULL" ? "Tam İade" : "Kısmi İade"}
                                                    </Badge>
                                                    {/* VOID, tutar kapsamı değil sağlayıcı işlemidir (aynı gün iptali). */}
                                                    {r.operationType === "VOID" && (
                                                        <Badge color="secondary" pill className="ms-1">İptal</Badge>
                                                    )}
                                                </td>
                                                <td>{PROVIDER_LABELS[r.provider] || r.provider || "-"}</td>
                                                <td>{refundActorLabel(r)}</td>
                                                <td>
                                                    <Badge color={st.color} pill>{st.label}</Badge>
                                                    {showStatusDetail && r.status === "FAILED" && r.failureMessage && (
                                                        <div className="text-muted small mt-1" title={r.failureMessage}>
                                                            {r.failureCode ? `${r.failureCode} — ` : ""}{r.failureMessage}
                                                        </div>
                                                    )}
                                                    {showStatusDetail && r.status === "SUCCESS" && r.referenceNo && (
                                                        <div className="text-muted small mt-1">Ref: {r.referenceNo}</div>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <Button
                                                        size="sm"
                                                        className="btn-rounded btn-navy-outline"
                                                        onClick={() => navigate(`/payment/${r.paymentId}`)}
                                                        title="Ödemenin tam detay sayfasını aç"
                                                    >
                                                        Detay <i className="bx bx-chevron-right" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>

                        <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small">Sayfa boyutu</span>
                                <Input
                                    type="select"
                                    style={{ width: 90 }}
                                    value={pageSize}
                                    onChange={(e) => changeSize(Number(e.target.value))}
                                >
                                    {DEFAULT_PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                                </Input>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    size="sm"
                                    className="btn-rounded btn-navy-outline"
                                    disabled={currentPage <= 0 || loading}
                                    onClick={() => goToPage(currentPage - 1)}
                                >
                                    <i className="bx bx-chevron-left" />
                                </Button>
                                <span className="text-muted small">
                                    {totalElements === 0
                                        ? "0 kayıt"
                                        : `${currentPage * pageSize + 1} - ${Math.min((currentPage + 1) * pageSize, totalElements)} / ${totalElements}`}
                                </span>
                                <Button
                                    size="sm"
                                    className="btn-rounded btn-navy-outline"
                                    disabled={currentPage + 1 >= totalPages || loading}
                                    onClick={() => goToPage(currentPage + 1)}
                                >
                                    <i className="bx bx-chevron-right" />
                                </Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default RefundHistoryTable;
